import React from "react";
import {App, Modal, Input, InputNumber, Switch, Select, DatePicker, Typography, Spin, Space, Button, Divider, Tooltip, Tag, Tabs} from "antd";
import {PlusOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "@/api/axios.js";
import "./ManualFieldModal.css";

const {Text} = Typography;
const {TextArea} = Input;
const {RangePicker} = DatePicker;

// Le sei tassonomie storiche sono gli unici campi "select" con un set di opzioni non gestito
// dall'anagrafica generica (vedi il ramo MULTISELECT/SELECT sotto per le altre). Appaiono tra i
// campi manuali sempre, AI attiva o no: un valore impostato qui si applica comunque sopra
// l'output AI (sostituendolo, o sommandosi per i campi multi-valore — vedi ai.controller.js#mergeManualOverrides).
const TAXONOMY = {
	categoria: {endpoint: "/api/categories", multiple: false},
	classe: {endpoint: "/api/classes", multiple: false},
	fondi: {endpoint: "/api/funds", multiple: false},
	bollino: {endpoint: "/api/badges", multiple: true},
	filtro: {endpoint: "/api/filters", multiple: true},
	meccanica: {endpoint: "/api/mechanics", multiple: true}
};

const DATE_FMT = "YYYY-MM-DD";
const DISPLAY_FMT = "DD/MM/YYYY";

// AI effettiva = globalmente abilitata AND non disabilitata per questa cooperativa. Stessa
// formula del backend (getCooperativaManualFields) — qui decide sia il badge "AI attiva" sia il
// raggruppamento in tab quando i campi sono troppi per un elenco unico.
function isAiActive(field)
{
	return !!field?.globalAiEnabled && !field?.aiDisabledForCoop;
}

function resolveKind(field)
{
	if (!field)
		return {kind: "text"};

	const tax = TAXONOMY[field.key];
	if (tax)
		return {kind: "taxonomy", multiple: tax.multiple, endpoint: tax.endpoint};

	const type = String(field.type || "").toUpperCase();

	// MULTISELECT non legacy = tassonomia generica servita da /api/taxonomy-values/<key>.
	// Prima questi campi cadevano nel fallback testo perché non erano nella mappa TAXONOMY,
	// che elenca solo le sei storiche.
	if (type === "MULTISELECT")
		return {kind: "taxonomy", multiple: true, endpoint: `/api/taxonomy-values/${field.key}`};

	// SELECT non legacy = tassonomia generica a valore singolo (stessa API del ramo sopra,
	// cardinalità diversa — es. banner/banner_collegato dopo il cutover del Task 15).
	if (type === "SELECT")
		return {kind: "taxonomy", multiple: false, endpoint: `/api/taxonomy-values/${field.key}`};

	if (type === "TEXTAREA")
		return {kind: "textarea"};
	if (type === "NUMBER")
		return {kind: "number"};
	if (type === "BOOLEAN")
		return {kind: "boolean"};
	if (type === "DATE")
		return {kind: field.dateMode === "range" ? "daterange" : "date"};

	// Nessun altro tipo ha una sorgente di opzioni propria → input testo (come l'editor card).
	return {kind: "text"};
}

function initialValueFor(meta, initialValue)
{
	if (meta.kind === "date")
		return initialValue ? dayjs(initialValue, DATE_FMT) : null;
	if (meta.kind === "daterange")
	{
		if (typeof initialValue === "string" && initialValue.includes("_"))
		{
			const [a, b] = initialValue.split("_");
			return [dayjs(a, DATE_FMT), dayjs(b, DATE_FMT)];
		}
		return null;
	}
	if (meta.kind === "boolean")
		return Boolean(initialValue);
	if (meta.kind === "taxonomy")
		return meta.multiple ? (Array.isArray(initialValue) ? initialValue : []) : (initialValue || undefined);
	if (meta.kind === "number")
		return initialValue === "" || initialValue == null ? null : initialValue;
	return initialValue ?? "";
}

function toPayload(meta, value)
{
	switch (meta.kind)
	{
		case "date":
			return value ? value.format(DATE_FMT) : null;
		case "daterange":
			return Array.isArray(value) && value[0] && value[1]
				? `${value[0].format(DATE_FMT)}_${value[1].format(DATE_FMT)}`
				: null;
		case "boolean":
			return Boolean(value);
		case "number":
			return value == null || value === "" ? null : value;
		case "taxonomy":
			return meta.multiple
				? (Array.isArray(value) && value.length ? value : null)
				: (value || null);
		default:
		{
			const s = typeof value === "string" ? value.trim() : value;
			return s ? s : null;
		}
	}
}

function computeLabel(meta, value, options)
{
	switch (meta.kind)
	{
		case "date":
			return value ? value.format(DISPLAY_FMT) : "";
		case "daterange":
			return Array.isArray(value) && value[0] && value[1]
				? `${value[0].format(DISPLAY_FMT)} – ${value[1].format(DISPLAY_FMT)}`
				: "";
		case "boolean":
			return value ? "Sì" : "No";
		case "number":
			return value == null || value === "" ? "" : String(value);
		case "taxonomy":
		{
			const map = new Map(options.map(o => [o.value, o.label]));
			if (meta.multiple)
				return (Array.isArray(value) ? value : []).map(v => map.get(v) || v).join(", ");
			return value ? (map.get(value) || String(value)) : "";
		}
		default:
		{
			const s = typeof value === "string" ? value.trim() : value;
			return s ? String(s) : "";
		}
	}
}

/**
 * Riga di un singolo campo manuale dentro la modale unificata: gestisce solo il proprio input e
 * notifica il valore corrente al genitore ad ogni cambio. Il salvataggio è unico e avviene tutto
 * insieme, per tutti i campi, quando l'utente preme "Applica" in fondo alla modale — per questo,
 * a differenza della vecchia modale a campo singolo, qui creare un valore tassonomia al volo
 * (Invio) seleziona e resta in editing, non applica/chiude nulla.
 */
function ManualFieldRow({field, cooperativaId, initialValue, onChange})
{
	const {message} = App.useApp();
	const meta = React.useMemo(() => resolveKind(field), [field]);
	const [value, setValue] = React.useState(() => initialValueFor(meta, initialValue));
	const [options, setOptions] = React.useState([]);
	const [loadingOptions, setLoadingOptions] = React.useState(false);
	const [taxSearch, setTaxSearch] = React.useState("");
	const [creating, setCreating] = React.useState(false);
	// Apertura del dropdown controllata: serve per poterlo chiudere esplicitamente dopo il click
	// sul pulsante "Aggiungi" (l'Invio invece lo lascia aperto per aggiungere più valori).
	const [dropdownOpen, setDropdownOpen] = React.useState(false);
	const valueRef = React.useRef(value);
	React.useEffect(() => { valueRef.current = value; }, [value]);

	// Carica le opzioni per i campi tassonomia.
	React.useEffect(() =>
	{
		if (meta.kind !== "taxonomy" || !cooperativaId)
		{
			setOptions([]);
			return undefined;
		}

		let cancelled = false;
		setLoadingOptions(true);
		api.get(meta.endpoint, {params: {cooperativaId}})
			.then(({data}) =>
			{
				if (cancelled)
					return;
				const items = Array.isArray(data?.items) ? data.items : [];
				setOptions(
					items
						.map(it => ({value: String(it.id || it._id), label: it.name || String(it.id || it._id)}))
						.sort((a, b) => a.label.localeCompare(b.label, "it", {sensitivity: "base"}))
				);
			})
			.catch(() => { if (!cancelled) setOptions([]); })
			.finally(() => { if (!cancelled) setLoadingOptions(false); });

		return () => { cancelled = true; };
	}, [meta.kind, meta.endpoint, cooperativaId]);

	// Notifica il genitore ad ogni cambio valore (incluso quello iniziale, appena montata): così
	// "Applica" ha sempre uno snapshot completo anche per i campi che l'utente non ha toccato.
	React.useEffect(() =>
	{
		onChange(field.key, toPayload(meta, value), computeLabel(meta, value, options));
	}, [value, options, meta, onChange, field.key]);

	// Crea al volo un nuovo valore di tassonomia (categoria/classe/…): lo salva in DB per la
	// cooperativa, lo aggiunge alle opzioni e lo seleziona — resta nella riga, non applica/chiude
	// (il salvataggio è unico e finale, tramite "Applica" in fondo alla modale).
	// closeAfter: chiude il dropdown a creazione avvenuta (click su "Aggiungi"); l'Invio passa
	// false così l'utente può digitare e confermare altri valori senza riaprire la select.
	const handleCreateTaxonomy = React.useCallback(async (closeAfter = false) =>
	{
		const name = taxSearch.trim();
		if (!name || meta.kind !== "taxonomy" || !meta.endpoint)
			return;
		if (!cooperativaId)
		{
			message.warning("Cooperativa non risolta: impossibile creare il valore");
			return;
		}

		setCreating(true);
		try
		{
			const {data} = await api.post(meta.endpoint, {name, cooperativaId});
			const item = data?.item || data?.category || data?.classe || data?.fund || data?.badge || data?.filtro || data?.meccanica || data;
			const id = item ? String(item.id || item._id || "") : "";
			if (!id)
			{
				message.error("Creazione non riuscita");
				return;
			}

			const newLabel = item.name || name;
			setOptions(prev => prev.some(o => o.value === id)
				? prev
				: [...prev, {value: id, label: newLabel}].sort((a, b) => a.label.localeCompare(b.label, "it", {sensitivity: "base"})));

			setValue(meta.multiple ? [...(Array.isArray(valueRef.current) ? valueRef.current : []), id] : id);
			setTaxSearch("");
			if (closeAfter)
				setDropdownOpen(false);
			message.success(`"${newLabel}" creato`);
		}
		catch (e)
		{
			message.error(e?.response?.data?.error || "Impossibile creare il valore");
		}
		finally
		{
			setCreating(false);
		}
	}, [taxSearch, meta, cooperativaId, message]);

	switch (meta.kind)
	{
		case "textarea":
			return <TextArea autoSize={{minRows: 2, maxRows: 6}} allowClear value={value} onChange={e => setValue(e.target.value)} placeholder="Valore"/>;
		case "number":
			return <InputNumber style={{width: "100%"}} value={value} onChange={setValue} placeholder="Valore"/>;
		case "boolean":
			return <Switch checked={Boolean(value)} onChange={setValue} checkedChildren="Sì" unCheckedChildren="No"/>;
		case "date":
			return <DatePicker style={{width: "100%"}} value={value} onChange={setValue} format={DISPLAY_FMT}/>;
		case "daterange":
			return <RangePicker style={{width: "100%"}} value={value} onChange={setValue} format={DISPLAY_FMT}/>;
		case "taxonomy":
		{
			const trimmedSearch = taxSearch.trim();
			const canCreate = !!trimmedSearch && !!cooperativaId
				&& !options.some(o => String(o.label).toLowerCase() === trimmedSearch.toLowerCase());
			return (
				<Select
					style={{width: "100%"}}
					mode={meta.multiple ? "multiple" : undefined}
					showSearch
					allowClear
					optionFilterProp="label"
					loading={loadingOptions || creating}
					value={value}
					onChange={setValue}
					// searchValue controllato SOLO in modalità multipla: dopo la creazione il testo
					// digitato viene azzerato (setTaxSearch("")) anche visivamente, non solo
					// internamente — altrimenti il valore appena creato resta visibile anche come
					// testo residuo accanto al nuovo tag. In modalità singola va lasciato non
					// controllato: la select alterna la visualizzazione tra "valore selezionato" e
					// "testo di ricerca" internamente, e controllarlo qui romperebbe quel passaggio.
					searchValue={meta.multiple ? taxSearch : undefined}
					onSearch={setTaxSearch}
					open={dropdownOpen}
					onInputKeyDown={(e) =>
					{
						// Invio conferma il valore nuovo (crea + seleziona) e LASCIA il dropdown aperto:
						// l'utente può scriverne un altro e ripetere, "Applica" salva tutto alla fine.
						if (e.key === "Enter" && canCreate)
						{
							e.preventDefault();
							e.stopPropagation();
							handleCreateTaxonomy(false);
						}
					}}
					onOpenChange={(opened) => { setDropdownOpen(opened); if (!opened) setTaxSearch(""); }}
					options={options}
					placeholder={cooperativaId ? "Seleziona o digita per creare…" : "Cooperativa non risolta"}
					disabled={!cooperativaId}
					// Il pulsante "Aggiungi" vive DENTRO il dropdown (popupRender): il dropdown è un
					// popup fluttuante che si sovrappone ai campi sottostanti, quindi un pulsante messo
					// fuori dalla Select finirebbe coperto dal popup aperto mentre si digita. Qui invece
					// è renderizzato SEMPRE in cima al popup — anche quando nessuna opzione combacia
					// (menu "Nessuna opzione") — così è sempre visibile e cliccabile.
					// notFoundContent NON deve mai essere null: per le Select SINGOLE, con lista
					// filtrata vuota + notFoundContent null, AntD chiude da solo il dropdown → scatta
					// onOpenChange(false) → azzera taxSearch → il pulsante "Aggiungi" sparisce nello
					// stesso istante. Tenendo sempre un contenuto (es. "Nessuna opzione") il dropdown
					// resta aperto e il pulsante nel popupRender resta visibile.
					notFoundContent={loadingOptions ? <Spin size="small"/> : "Nessuna opzione"}
					popupRender={(menu) => (
						<>
							{canCreate && (
								<>
									<div
										className="manual-field-row-add"
										onMouseDown={(ev) => ev.preventDefault()}
									>
										<Button
											type="text"
											size="small"
											block
											icon={<PlusOutlined/>}
											loading={creating}
											onClick={() => handleCreateTaxonomy(true)}
										>
											{`Aggiungi "${trimmedSearch}"`}
										</Button>
									</div>
									<Divider style={{margin: "4px 0"}}/>
								</>
							)}
							{menu}
						</>
					)}
				/>
			);
		}
		default:
			return <Input allowClear value={value} onChange={e => setValue(e.target.value)} placeholder="Valore"/>;
	}
}

/**
 * Modale unificata dei campi manuali di una gabbia (prodotto o banner): elenca tutti i campi
 * disponibili come righe di un form, ciascuna col proprio input; un solo "Applica" in fondo
 * salva tutti i campi insieme in una sola chiamata.
 */
export default function ManualFieldModal({open, fields = [], cooperativaId, targetCount = 0, initialValues = {}, onApply, onCancel})
{
	const [submitting, setSubmitting] = React.useState(false);
	const pendingRef = React.useRef({});

	// Pre-semina lo snapshot con i valori correnti non appena la modale si apre: garantisce che
	// "Applica" trovi sempre una voce per ogni campo, anche per un click immediato prima che le
	// righe finiscano di notificare il proprio valore iniziale.
	React.useEffect(() =>
	{
		if (!open)
			return;
		const next = {};
		for (const field of fields)
			next[field.key] = {value: initialValues[field.key] ?? null, label: ""};
		pendingRef.current = next;
	}, [open, fields, initialValues]);

	const handleRowChange = React.useCallback((key, value, label) =>
	{
		pendingRef.current[key] = {value, label};
	}, []);

	// Divide in due gruppi solo quando SERVE: se una cooperativa non ha nessun campo con AI attiva
	// (il caso comune oggi) l'elenco resta piatto come prima, nessuna tab in piu' da vedere.
	const manualOnlyFields = React.useMemo(() => fields.filter((f) => !isAiActive(f)), [fields]);
	const aiActiveFields = React.useMemo(() => fields.filter((f) => isAiActive(f)), [fields]);
	const showTabs = manualOnlyFields.length > 0 && aiActiveFields.length > 0;

	const renderFieldRow = React.useCallback((field) =>
	{
		const meta = resolveKind(field);
		const aiActive = isAiActive(field);
		return (
			<div key={field.key} className="manual-field-row">
				<div className="manual-field-row-title">
					<Text strong className="manual-field-row-label">{field.name}</Text>
					{meta.kind === "taxonomy" && (
						<Tooltip title="Nei campi a elenco puoi digitare un valore nuovo e premere Invio per aggiungerlo prima di Applicare.">
							<QuestionCircleOutlined className="manual-field-row-help"/>
						</Tooltip>
					)}
					{aiActive && (
						<Tooltip title={meta.kind === "taxonomy" && meta.multiple
							? "I tag inseriti qui si aggiungono a quelli trovati dall'AI, senza duplicati."
							: "Il valore inserito qui sostituisce quello trovato dall'AI."}>
							<Tag color="blue" className="manual-field-row-ai-tag">AI attiva</Tag>
						</Tooltip>
					)}
				</div>
				<ManualFieldRow
					field={field}
					cooperativaId={cooperativaId}
					initialValue={initialValues[field.key]}
					onChange={handleRowChange}
				/>
			</div>
		);
	}, [cooperativaId, initialValues, handleRowChange]);

	const handleApply = React.useCallback(async () =>
	{
		if (!onApply)
			return;
		// Si inviano solo i campi cambiati rispetto al valore con cui la modale si è aperta: un
		// campo lasciato com'era non viene mai riscritto né svuotato. Serve per i campi che
		// appaiono vuoti pur avendo un valore (gabbie selezionate con valori diversi) e per i
		// banner, i cui valori possono cambiare nell'editor card senza tornare sulla gabbia.
		const payload = fields
			.map((field) =>
			{
				const row = pendingRef.current[field.key] || {value: null, label: ""};
				return {field, fieldKey: field.key, value: row.value, label: row.label};
			})
			.filter(({field, value}) =>
			{
				const meta = resolveKind(field);
				const baseline = toPayload(meta, initialValueFor(meta, initialValues[field.key]));
				return JSON.stringify(value ?? null) !== JSON.stringify(baseline ?? null);
			})
			.map(({fieldKey, value, label}) => ({fieldKey, value, label}));
		if (!payload.length)
		{
			onCancel?.();
			return;
		}

		setSubmitting(true);
		try
		{
			await onApply(payload);
		}
		finally
		{
			setSubmitting(false);
		}
	}, [onApply, onCancel, fields, initialValues]);

	return (
		<Modal
			open={open}
			title={targetCount > 1 ? `Imposta campi manuali (${targetCount} gabbie)` : "Imposta campi manuali"}
			onCancel={onCancel}
			maskClosable={!submitting}
			destroyOnHidden
			width={480}
			footer={[
				<Button key="cancel" onClick={onCancel} disabled={submitting}>Annulla</Button>,
				<Button key="apply" type="primary" loading={submitting} disabled={!fields.length} onClick={handleApply}>Applica</Button>
			]}
		>
			<Space direction="vertical" size={10} style={{width: "100%"}}>
				<Text type="secondary">
					{targetCount > 1
						? `Verrà applicato a ${targetCount} gabbie selezionate.`
						: "Verrà applicato alla gabbia selezionata."}
				</Text>
				{!fields.length && <Text type="secondary">Nessun campo disponibile.</Text>}
				{fields.length > 0 && !showTabs && fields.map(renderFieldRow)}
				{showTabs && (
					<Tabs
						size="small"
						items={[
							{
								key: "manual",
								label: `Campi manuali (${manualOnlyFields.length})`,
								children: (
									<Space direction="vertical" size={10} style={{width: "100%"}}>
										{manualOnlyFields.map(renderFieldRow)}
									</Space>
								)
							},
							{
								key: "ai",
								label: `Campi con AI (${aiActiveFields.length})`,
								children: (
									<Space direction="vertical" size={10} style={{width: "100%"}}>
										{aiActiveFields.map(renderFieldRow)}
									</Space>
								)
							}
						]}
					/>
				)}
			</Space>
		</Modal>
	);
}
